import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pause, Play, ShieldCheck, MagnifyingGlass, ArrowSquareOut } from "@phosphor-icons/react";
import API from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PauseAuditModal from "./components/PauseAuditModal";
import BrandWidePause from "./components/BrandWidePause";

const NETWORKS = [
  "all", "impact", "partnerize", "skimlinks", "mrge", "cj",
  "admedia", "pubadhub", "pubadhub_kevin", "shopnomix_kevin", "flexoffers",
  "siteplug", "maxbounty", "cashbackhive", "fatcoupon", "giddyup", "yieldkit", "shopnomix", "pronomix",
  "rga", "rga_vishy", "rga_ashok",
];

/**
 * Pause Center — centralised view + control for publisher_brand
 * status. Defaults to showing only the paused assignments because
 * that's the question admins land here to answer ("what's currently
 * paused, and is it actually blocking traffic?"). Toggle to "all" to
 * pause something new directly from here.
 *
 * Per-row actions:
 *   • Audit — opens PauseAuditModal so admin can verify enforcement
 *   • Pause / Activate — hits PUT /admin/publisher-brands/{pid}/{bid}/status
 *   • View publisher → opens publisher detail in a new tab
 */
export default function AdminPauseCenter() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [statusFilter, setStatusFilter] = useState("paused");
  const [networkFilter, setNetworkFilter] = useState("all");
  const [pubQ, setPubQ] = useState("");
  const [brandQ, setBrandQ] = useState("");
  const [pubQInput, setPubQInput] = useState("");
  const [brandQInput, setBrandQInput] = useState("");
  const [auditRow, setAuditRow] = useState(null);
  const [mode, setMode] = useState("assignments"); // assignments | brands

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/admin/pause-center/assignments", {
        params: {
          status: statusFilter,
          publisher_q: pubQ || undefined,
          brand_q: brandQ || undefined,
          network: networkFilter && networkFilter !== "all" ? networkFilter : undefined,
          page,
          page_size: 50,
        },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, networkFilter, pubQ, brandQ, page]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Debounce text inputs (300ms) — same UX as the brands page.
  useEffect(() => {
    const id = setTimeout(() => { setPubQ(pubQInput); setPage(1); }, 300);
    return () => clearTimeout(id);
  }, [pubQInput]);
  useEffect(() => {
    const id = setTimeout(() => { setBrandQ(brandQInput); setPage(1); }, 300);
    return () => clearTimeout(id);
  }, [brandQInput]);

  useEffect(() => { setPage(1); }, [statusFilter, networkFilter]);

  const toggleStatus = async (row) => {
    const next = row.status === "active" ? "paused" : "active";
    try {
      await API.put(
        `/admin/publisher-brands/${row.publisher_id}/${row.brand_id}/status`,
        { status: next }
      );
      toast.success(
        next === "paused"
          ? `Paused ${row.brand_name} for ${row.publisher_email}`
          : `Reactivated ${row.brand_name} for ${row.publisher_email}`
      );
      fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="pause-center-page">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Pause Center</h1>
        <p className="text-sm text-slate-500 mt-1 leading-snug">
          Centralised view of paused publisher-brand assignments. Audit any row to verify the tracker
          is actually blocking traffic, or pause/reactivate directly from here.
        </p>
      </div>

      <div className="flex items-center border border-slate-200 rounded-md overflow-hidden h-9 w-fit mb-4" data-testid="pause-center-mode">
        {[["assignments", "By assignment"], ["brands", "By brand · all publishers"]].map(([m, label]) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`px-3 h-9 text-xs font-semibold transition-colors ${
              mode === m ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50"
            }`}
            data-testid={`pause-center-mode-${m}`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === "brands" ? <BrandWidePause networks={NETWORKS} /> : (<>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search publisher (email or sub_id)…"
            value={pubQInput}
            onChange={(e) => setPubQInput(e.target.value)}
            className="pl-9 h-10"
            data-testid="pause-center-pub-search"
          />
        </div>
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search brand (name or id)…"
            value={brandQInput}
            onChange={(e) => setBrandQInput(e.target.value)}
            className="pl-9 h-10"
            data-testid="pause-center-brand-search"
          />
        </div>

        <select
          value={networkFilter}
          onChange={(e) => setNetworkFilter(e.target.value)}
          className="h-10 px-3 text-sm border border-slate-200 rounded-md bg-white font-mono"
          data-testid="pause-center-network-filter"
        >
          {NETWORKS.map((n) => (
            <option key={n} value={n}>{n === "all" ? "All feeds" : n}</option>
          ))}
        </select>

        <div className="flex items-center border border-slate-200 rounded-md overflow-hidden h-10" data-testid="pause-center-status-filter">
          {["paused", "active", "all"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`px-3 h-10 text-xs font-semibold uppercase tracking-wider transition-colors ${
                statusFilter === s
                  ? "bg-slate-900 text-white"
                  : "bg-white text-slate-500 hover:bg-slate-50"
              }`}
              data-testid={`pause-center-status-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="text-xs text-slate-500 mb-2 font-mono" data-testid="pause-center-summary">
        {total.toLocaleString()} assignment{total === 1 ? "" : "s"} match · status={statusFilter}
        {networkFilter !== "all" ? ` · feed=${networkFilter}` : ""}
      </div>

      {/* Table */}
      <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-bold">Brand</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-bold">Publisher</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-bold">Feed</th>
              <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-bold">Status</th>
              <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400 text-sm">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400 text-sm">
                No assignments match the current filters.
              </td></tr>
            ) : items.map((row) => (
              <tr key={`${row.publisher_id}-${row.brand_id}`} data-testid={`pause-center-row-${row.brand_id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-800">{row.brand_name || "—"}</div>
                  <div className="text-xs text-slate-400 font-mono">{row.brand_id}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-slate-700">{row.publisher_email || row.publisher_id}</div>
                  <div className="text-xs text-slate-400 font-mono">{row.publisher_sub_id}</div>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-600">{row.network}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    row.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                  }`}>{row.status}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    {row.status === "paused" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs text-slate-500 hover:text-emerald-700"
                        onClick={() => setAuditRow(row)}
                        title="Verify whether the tracker is actually blocking this paused brand"
                        data-testid={`pause-center-audit-${row.brand_id}`}
                      >
                        <ShieldCheck size={14} className="mr-1" /> Audit
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={() => toggleStatus(row)}
                      data-testid={`pause-center-toggle-${row.brand_id}`}
                    >
                      {row.status === "active"
                        ? <><Pause size={14} className="mr-1" /> Pause</>
                        : <><Play size={14} className="mr-1" /> Activate</>}
                    </Button>
                    <Link
                      to={`/admin/publishers/${row.publisher_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center px-2 h-7 text-xs text-slate-400 hover:text-slate-700"
                      title="Open publisher detail"
                      data-testid={`pause-center-pub-link-${row.brand_id}`}
                    >
                      <ArrowSquareOut size={14} />
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-3 flex items-center justify-between text-xs text-slate-500" data-testid="pause-center-pagination">
          <span className="font-mono">Page {page} of {totalPages} · {total.toLocaleString()} total</span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="pause-center-prev">
              Prev
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="pause-center-next">
              Next
            </Button>
          </div>
        </div>
      )}

      <PauseAuditModal
        open={!!auditRow}
        onClose={() => setAuditRow(null)}
        publisherId={auditRow?.publisher_id}
        brandId={auditRow?.brand_id}
        brandName={auditRow?.brand_name}
      />
      </>)}
    </div>
  );
}
